import { Button, Dialog, Strong, toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { useRemoveTeamMember } from '../../hooks'
import type { TeamMember } from '../../hooks'
import { getDisplayName } from '../../utils/getDisplayName'

import type { JSX } from 'react'

interface NativeConfirmRemoveMemberDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
  member: TeamMember | null
}

export const NativeConfirmRemoveMemberDialog = ({
  isOpen,
  onOpenChange,
  onClose,
  member,
}: NativeConfirmRemoveMemberDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const { removeTeamMember, loading } = useRemoveTeamMember()

  const handleRemove = async (): Promise<void> => {
    if (!organizationId || !member) return

    const result = await removeTeamMember({
      organizationId,
      accountId: member.id,
    })

    if (result.success) {
      toast.success(
        <>
          <Strong>{getDisplayName(member)}</Strong> has been removed from the
          team
        </>,
      )
      onClose()
    } else {
      toast.error(result.error ?? 'Failed to remove team member')
    }
  }

  if (!member) return <></>

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Remove Team Member</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Description>
            Are you sure you want to remove{' '}
            <strong>{getDisplayName(member)}</strong> from this team? This user
            will no longer have access to any assigned channels.
          </Dialog.Description>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="critical"
            onClick={handleRemove}
            disabled={loading}
            loading={loading}
          >
            Yes, Remove
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
